import { Component } from '@theme/component';

/**
 * Favoritos de MAMU.
 *
 * - `<mamu-favorites>` (lo pinta snippets/mamu-js-loader.liquid en todas las páginas): activa los botones
 *   `[data-mamu-fav]` (tarjetas, ficha, vista rápida), guarda los handles en `localStorage['mamu:favs']`,
 *   actualiza `[data-mamu-fav-count]` del header, lanza el toast y se sincroniza entre pestañas.
 * - `<mamu-favorites-list>` (sections/mamu-favoritos.liquid): pinta las tarjetas guardadas pidiendo
 *   `/products/<handle>?section_id=mamu-product-card-render` (la tarjeta es idéntica a la de las colecciones).
 *
 * Evento que emite: `mamu:favs-change` en `document`, con `detail = { favs, handle, action, source }`.
 */

const STORAGE_KEY = 'mamu:favs';
const MAX_FAVS = 100;
const CARD_SECTION = 'mamu-product-card-render';

/* -------------------------------------------------------------------------------------------------
 * Textos (los reales vienen de locales/* vía <script id="mamu-i18n">; estos son el respaldo)
 * ----------------------------------------------------------------------------------------------- */

const DEFAULT_STRINGS = {
  'toast.fav_added': 'Agregado a favoritos',
  'toast.fav_removed': 'Quitado de favoritos',
  'toast.fav_view': 'Ver favoritos',
  'favs.count_one': '{n} pieza guardada',
  'favs.count_other': '{n} piezas guardadas',
};

/** @type {Record<string, string> | null} */
let strings = null;

/**
 * @param {string} key
 * @param {Record<string, string | number>} [vars]
 * @returns {string}
 */
function t(key, vars = {}) {
  if (!strings) {
    strings = { ...DEFAULT_STRINGS };
    try {
      const raw = document.getElementById('mamu-i18n')?.textContent;
      if (raw) Object.assign(strings, JSON.parse(raw));
    } catch {
      /* se queda el respaldo en español */
    }
  }

  let text = strings[key] ?? key;
  for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, String(value));
  return text;
}

/* -------------------------------------------------------------------------------------------------
 * Almacén (localStorage con respaldo en memoria si el navegador lo bloquea)
 * ----------------------------------------------------------------------------------------------- */

/** @type {string[] | null} */
let memory = null;

/** @type {string[]} */
let favs = [];

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function sanitize(value) {
  if (!Array.isArray(value)) return [];

  /** @type {string[]} */
  const out = [];
  for (const handle of value) {
    if (typeof handle === 'string' && handle && handle.length <= 255 && !out.includes(handle)) out.push(handle);
  }
  return out.slice(-MAX_FAVS);
}

/** @returns {string[]} */
function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return sanitize(raw ? JSON.parse(raw) : []);
  } catch {
    return memory ?? [];
  }
}

/** @param {string[]} list */
function save(list) {
  memory = list;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* sin almacenamiento: los favoritos viven mientras la pestaña esté abierta */
  }
}

/**
 * @param {Record<string, unknown>} detail
 */
function emitChange(detail) {
  document.dispatchEvent(new CustomEvent('mamu:favs-change', { detail: { favs: [...favs], ...detail } }));
}

/**
 * Agrega o quita un handle.
 *
 * @param {string} handle
 * @returns {'added' | 'removed'}
 */
function toggleFav(handle) {
  const action = favs.includes(handle) ? 'removed' : 'added';
  favs = action === 'removed' ? favs.filter((h) => h !== handle) : sanitize([...favs, handle]);
  save(favs);
  emitChange({ handle, action, source: 'local' });
  return action;
}

/**
 * Quita un handle sin toast (producto que ya no existe).
 *
 * @param {string} handle
 */
function pruneFav(handle) {
  if (!favs.includes(handle)) return;
  favs = favs.filter((h) => h !== handle);
  save(favs);
  emitChange({ handle, action: 'pruned', source: 'local' });
}

/* -------------------------------------------------------------------------------------------------
 * Controlador global: botones, contador, toast, pestañas
 * ----------------------------------------------------------------------------------------------- */

/**
 * @extends {Component}
 */
class MamuFavorites extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    favs = load();
    this.#sync();

    // En captura y deteniendo la propagación: el botón vive dentro de tarjetas con enlace y `on:click` de Horizon.
    document.addEventListener('click', this.#onClick, { capture: true, signal });
    document.addEventListener('mamu:favs-change', this.#onChange, { signal });
    window.addEventListener('storage', this.#onStorage, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    // Botones que llegan después (paginación, filtros, vista rápida, Section Rendering): se activan en cuanto entran al DOM.
    this.#observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('[data-mamu-fav]') || node.querySelector('[data-mamu-fav]')) {
            this.#sync();
            return;
          }
        }
      }
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-mamu-fav]') : null;
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = target.dataset.handle;
    if (!handle) return;

    const action = toggleFav(handle);

    document.dispatchEvent(
      new CustomEvent('mamu:toast', {
        detail:
          action === 'added'
            ? {
                key: 'fav',
                icon: 'heart',
                message: t('toast.fav_added'),
                actionLabel: t('toast.fav_view'),
                actionHref: this.dataset.url,
              }
            : { key: 'fav', icon: 'heart', message: t('toast.fav_removed') },
      })
    );
  };

  /**
   * Otra pestaña cambió la lista.
   *
   * @param {StorageEvent} event
   */
  #onStorage = (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;

    favs = load();
    emitChange({ handle: null, action: 'sync', source: 'storage' });
  };

  /**
   * Volver con "atrás" puede traer la página desde la caché con una lista vieja.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    favs = load();
    emitChange({ handle: null, action: 'sync', source: 'pageshow' });
  };

  /** Cambió la lista (aquí o en otra pestaña): botones y contador al instante. */
  #onChange = () => {
    this.#sync();
  };

  /** Pone al día botones y contadores con la lista actual. */
  #sync() {
    for (const button of document.querySelectorAll('[data-mamu-fav]')) {
      if (!(button instanceof HTMLElement)) continue;

      const pressed = favs.includes(button.dataset.handle ?? '');
      const label = pressed ? button.dataset.labelRemove : button.dataset.labelAdd;

      button.setAttribute('aria-pressed', String(pressed));
      if (label) button.setAttribute('aria-label', label);
    }

    for (const counter of document.querySelectorAll('[data-mamu-fav-count]')) {
      if (!(counter instanceof HTMLElement)) continue;

      counter.textContent = String(favs.length);
      counter.hidden = favs.length === 0;
    }
  }
}

/* -------------------------------------------------------------------------------------------------
 * Página Favoritos
 * ----------------------------------------------------------------------------------------------- */

/** @type {Map<string, Promise<{ html: string } | { gone: true }>>} */
const cardRequests = new Map();

/**
 * Pide la tarjeta de un producto a la Section Rendering API. Una petición por handle y por carga de página.
 *
 * @param {string} handle
 * @returns {Promise<{ html: string } | { gone: true }>}
 */
function requestCard(handle) {
  let request = cardRequests.get(handle);

  if (!request) {
    const root = /** @type {any} */ (window).Shopify?.routes?.root ?? '/';
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION}`;

    request = fetch(url, { headers: { Accept: 'text/html' } })
      .then(async (response) => {
        if (response.status === 404) return { gone: true };
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return { html: await response.text() };
      })
      .catch((error) => {
        // Un fallo de red no se guarda: el botón "Reintentar" vuelve a pedirla.
        cardRequests.delete(handle);
        throw error;
      });

    cardRequests.set(handle, /** @type {Promise<{ html: string } | { gone: true }>} */ (request));
  }

  return /** @type {Promise<{ html: string } | { gone: true }>} */ (request);
}

/**
 * Lista de tarjetas favoritas.
 *
 * @typedef {object} ListRefs
 * @property {HTMLElement} grid - Rejilla donde van las tarjetas.
 * @property {HTMLElement} [count] - Texto "N piezas guardadas".
 *
 * @extends {Component<ListRefs>}
 */
class MamuFavoritesList extends Component {
  requiredRefs = ['grid'];

  /** @type {AbortController | null} */
  #controller = null;

  /** Para descartar respuestas de una pasada vieja. */
  #run = 0;

  /** Se quitó la última tarjeta con el foco dentro: falta pasarlo al estado vacío. */
  #focusEmpty = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();

    document.addEventListener('mamu:favs-change', this.#onChange, { signal: this.#controller.signal });

    // El contrato del controlador global puede tardar un instante: se lee la lista aquí también.
    if (!favs.length) favs = load();
    this.#reconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
    this.#run++;
  }

  /** Botón "Reintentar" (on:click). */
  retry() {
    this.#reconcile();
  }

  #onChange = () => {
    this.#reconcile();
  };

  /**
   * @param {'loading' | 'ready' | 'empty' | 'error'} state
   */
  #setState(state) {
    this.dataset.state = state;
  }

  /** @returns {Map<string, HTMLElement>} */
  #items() {
    /** @type {Map<string, HTMLElement>} */
    const map = new Map();
    for (const item of this.refs.grid.children) {
      if (item instanceof HTMLElement && item.dataset.handle) map.set(item.dataset.handle, item);
    }
    return map;
  }

  /** @param {number} total */
  #updateCount(total) {
    const { count } = this.refs;
    if (!count) return;

    count.textContent = total > 0 ? t(total === 1 ? 'favs.count_one' : 'favs.count_other', { n: total }) : '';
  }

  /** Pone la rejilla al día con la lista: quita lo que sobra, pide lo que falta y ordena. */
  async #reconcile() {
    const run = ++this.#run;
    const { grid } = this.refs;
    const handles = [...favs];
    let items = this.#items();

    for (const [handle, item] of items) {
      if (!handles.includes(handle)) this.#removeItem(item);
    }
    items = this.#items();

    if (handles.length === 0) {
      this.#updateCount(0);
      this.#setState('empty');
      this.#flushFocus();
      return;
    }

    const missing = handles.filter((handle) => !items.has(handle));

    if (missing.length) {
      if (items.size === 0) this.#setState('loading');

      const results = await Promise.allSettled(missing.map(requestCard));
      if (run !== this.#run) return;

      let failed = false;

      results.forEach((result, index) => {
        const handle = missing[index];
        if (!handle) return;

        if (result.status === 'rejected') {
          failed = true;
          return;
        }

        const node = 'html' in result.value ? this.#parseCard(result.value.html) : null;

        if (node) {
          grid.append(node);
        } else {
          // 404 o sin tarjeta: el producto ya no está publicado.
          pruneFav(handle);
        }
      });

      if (failed && this.#items().size === 0) {
        this.#setState('error');
        return;
      }
    }

    // Orden de la lista guardada.
    const ordered = this.#items();
    handles.forEach((handle, index) => {
      const node = ordered.get(handle);
      if (node && grid.children[index] !== node) grid.insertBefore(node, grid.children[index] ?? null);
    });

    const total = this.#items().size;
    this.#updateCount(total);
    this.#setState(total > 0 ? 'ready' : 'empty');
    this.#flushFocus();
  }

  /**
   * Convierte la respuesta de la sección en un nodo vivo e inyecta (una sola vez) el CSS de la tarjeta.
   *
   * @param {string} html
   * @returns {HTMLElement | null}
   */
  #parseCard(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const card = doc.querySelector('[data-mamu-card-root]');
    if (!(card instanceof HTMLElement)) return null;

    if (!this.querySelector('style[data-mamu-card-css]')) {
      const css = doc.querySelector('style[data-section-stylesheet]');
      if (css?.textContent) {
        const style = document.createElement('style');
        style.dataset.mamuCardCss = '';
        style.textContent = css.textContent;
        this.prepend(style);
      }
    }

    const node = document.importNode(card, true);
    return node instanceof HTMLElement ? node : null;
  }

  /**
   * Quita una tarjeta y, si tenía el foco, lo pasa a la siguiente. Si no queda ninguna, el foco va al estado vacío
   * en cuanto este se muestra (un elemento oculto no puede recibir el foco).
   *
   * @param {HTMLElement} item
   */
  #removeItem(item) {
    const hadFocus = item.contains(document.activeElement);
    const next = item.nextElementSibling ?? item.previousElementSibling;
    item.remove();

    if (!hadFocus) return;

    const target = next?.querySelector('a[href], button');
    if (target instanceof HTMLElement) target.focus();
    else this.#focusEmpty = true;
  }

  /** Foco en el estado vacío, si se quedó pendiente al quitar la última tarjeta. */
  #flushFocus() {
    if (!this.#focusEmpty) return;

    this.#focusEmpty = false;
    const target = this.querySelector('[data-mamu-favs-focus]');
    if (target instanceof HTMLElement) target.focus();
  }
}

if (!customElements.get('mamu-favorites')) {
  customElements.define('mamu-favorites', MamuFavorites);
}

if (!customElements.get('mamu-favorites-list')) {
  customElements.define('mamu-favorites-list', MamuFavoritesList);
}
